import classnames from 'classnames';
import React, {
  forwardRef,
  RefAttributes,
  type ComponentType,
  type CSSProperties,
  type MouseEvent,
  type ReactElement,
} from 'react';

import { Ratings } from '@/core/tamagoshi/Ratings/Ratings';
import { type CurrencyType } from '@/domains/core/tamagoshi/Price/Price.interfaces';

import { AddToCartButton } from './components/AddToCartButton/AddToCartButton';
import { BrandImage } from './components/BrandImage/BrandImage';
import {
  DeliveryLabel,
  type DeliveryLabelProps,
} from './components/DeliveryLabel/DeliveryLabel';
import {
  DetailedPrice,
  type DetailedPriceTranslations,
} from './components/DetailedPrice/DetailedPrice';
import { type ImageProps } from './components/Image/Image';
import { LegalMention } from './components/LegalMention/LegalMention';
import { ModelVariations } from './components/ModelVariations/ModelVariations';
import { ProductImage } from './components/ProductImage/ProductImage';
import { ProductTags } from './components/ProductTags/ProductTags';
import { Title } from './components/Title/Title';
import {
  MiddleLabel,
  ThirdLineLabel,
  TopLeftLabel,
  type ProductCardListingsTranslations,
} from './interfaces';

import styles from './ProductCardListings.module.scss';

export const DEFAULT_TAG = 'div' as const;

export interface ProductCardListingsProps<
  Tag extends React.ElementType = typeof DEFAULT_TAG,
> {
  /**
   * title of the product
   */
  title: string;
  /**
   * The currency
   */
  currency: CurrencyType;
  /**
   * main price
   */
  price: number;
  /**
   * retail price is the crossed out price
   */
  retailPrice?: number;
  /**
   * An alternative price bellow main
   * (eg => main: 11€50/m2   secondary: 92€ per unit)
   */
  secondaryPrice?: number;
  /**
   * Unit to display for the secondary price
   * This unit is displayed after the price and tax information and separated by a slash
   */
  secondaryUnit?: string;
  /**
   * A label to display the time left label before discount ending.
   * This props override `secondaryPrice` to `undefined`.
   */
  retailPriceTimeLeftLabel?: string;
  /**
   * product image source url
   */
  imageSrc: string;
  /**
   * product image sourceSet url
   */
  imageSrcSet?: string;
  /**
   * User can use its own advanced Image component where he can manage lazyloading/placeholder.
   */
  imageComponent?: ComponentType<ImageProps> | ReactElement<ImageProps>;
  /**
   * User can use its own advanced Image component where he can manage lazyloading/placeholder.
   * This variant of ImageListingsComponent includes several images in a carousel
   */
  productImage?: ComponentType<ImageProps> | ReactElement<ImageProps>;
  /**
   * Indicates how the browser should load the image
   */
  imageLoading?: 'eager' | 'lazy';
  /**
   * Add data-src attribute in product image tag if imageLoading is `lazy`. It's
   * mainly used for seo
   */
  provideImageDataSrc?: boolean;
  /**
   * brand image source url
   */
  brandImageSrc?: string;
  /**
   * brand name text
   */
  brandName?: string;
  /**
   * display a text like "42 models for this product"
   */
  modelsCount?: number;
  /**
   * display rating starts
   */
  rating?: number;
  /**
   * display rating count in parentheses
   */
  ratingCount?: number;
  /**
   * Use in german platform to display legal price text
   */
  legalMention?: boolean;
  /**
   * Used in the German platform to display legal price text for products with 0 VAT instead of the usual legal mention
   */
  legalMentionNoVAT?: boolean;
  /**
   * Use to display specific displays on b2b platform. If it's true it take
   * `price` props as without VAT price and `secondaryPrice` prop as VAT price
   */
  b2b?: boolean;
  /**
   * Identify the product has selected for pro
   */
  pro?: boolean;
  /**
   * Used to display delivery information
   */
  delivery?: DeliveryLabelProps['deliveryLabel'];
  /**
   * the label to display in the product card top left corner, it can be bulk, or coupon, or pro price
   */
  topLeftLabel?: TopLeftLabel[];
  /**
   * the labels to display in the middle product card, it can be sponsored and one among the following : promo / sales / blackfriday / manodays / top sales / best quality offer / eco responsible / is local
   */
  middleLabels?: MiddleLabel[];
  /**
   * the labels to display under the product card title, it can be technical info / eco responsible / is local
   */
  thirdLineLabels?: ThirdLineLabel[];
  /**
   * Identify if the product has a promo
   */
  promo?: boolean;
  /**
   * Change the display of the product card to light
   */
  className?: string;
  /**
   * Root tag
   */
  tag?: Tag;
  /**
   * Component translations for legal mentions, models count and others
   */
  translations: ProductCardListingsTranslations;
  /**
   * Used to display the add to cart wrapper component, only available with the vertical version of the ProductCardListings
   */
  addToCart?: boolean;
  /**
   * Used to tag a product as "made in a specific country" to underline its
   * local characteristic. For now only France is supported
   */
  madeIn?: 'fr';
  /**
   * Used to switch the presentation type of the card, defaults to "technical"
   */
  presentation?: 'technical' | 'inspirational';
  /**
   * Used to tag a product as following eco responsible principles
   */
  ecoResponsible?: boolean;
  /**
   * Used to know if we should display second hand tag
   */
  secondHand?: boolean;
  /**
   * Used to intercept clicks on ProductCardListings (ie : tracking, action dispatching…)
   */
  onClick?(event: MouseEvent<HTMLElement>): unknown;

  /**
   * Used to intercept clicks on add to cart button (only on promoted)
   */
  onAddToCart?(event: MouseEvent): void;

  /**
   * Allow custom inline css
   */
  style?: CSSProperties;

  /**
   * If you set a custom component as main tag, you can pass props to it by this
   * props
   */
  tagProps?: React.ComponentPropsWithoutRef<Tag>;
  /**
   * Price shown with or without including VAT
   * If it's 'withoutVAT' it takes `price` and `retail` props as without VAT price and `secondaryPrice` prop as VAT price
   * Else if it is 'VAT', `price` and `retail` props is displayed all taxes included and `secondaryPrice` prop as without VAT price
   * Undefined to show no tax label
   */
  tax?: 'withoutVAT' | 'VAT';
  /** Classname for popover content */
  popoverContentClassName?: string;
  promoted?: boolean;
  technicalInfoEnabled?: boolean;
  /**
   * Used to intercept clicks on sponsoredLabel (ie : tracking, action
   * dispatching…)
   */
  onAdvertiserLabelClicked?(event: MouseEvent<HTMLElement>): unknown;
}

/**
 * The new product card can be full controlled by his props.
 */
function InnerProductCardListings<
  Tag extends React.ElementType = typeof DEFAULT_TAG,
>(
  props: ProductCardListingsProps<Tag>,
  ref: React.ForwardedRef<React.ElementRef<Tag>>,
) {
  const {
    tag = DEFAULT_TAG,
    className,
    title,
    currency,
    price,
    secondaryPrice,
    secondaryUnit,
    retailPrice,
    retailPriceTimeLeftLabel,
    imageSrc,
    imageSrcSet,
    imageLoading = 'eager',
    provideImageDataSrc = false,
    imageComponent: ImageComponent,
    productImage,
    brandImageSrc,
    brandName = '',
    modelsCount = 1,
    rating = 0,
    ratingCount,
    legalMention = false,
    legalMentionNoVAT = false,
    translations,
    onClick,
    b2b = false,
    pro = false,
    delivery,
    topLeftLabel,
    middleLabels,
    thirdLineLabels,
    onAddToCart,
    madeIn,
    ecoResponsible = false,
    tagProps = {},
    tax,
    secondHand = false,
    presentation = 'technical',
    popoverContentClassName,
    promoted,
    technicalInfoEnabled,
    promo,
    onAdvertiserLabelClicked,
    ...otherProps
  } = props;
  const type = promoted ? 'PROMOTED' : 'DEFAULT';

  const rootClassNames = classnames(
    styles.root,
    {
      [styles.informationPlaceholder]: modelsCount <= 1,
      [styles.b2b]: b2b,
      [styles.promoted]: promoted,
      [styles.technical]: presentation === 'technical',
      [styles.inspirational]: presentation === 'inspirational',
      [styles.withTechnicalInfoTags]:
        technicalInfoEnabled && !!thirdLineLabels?.length, // REMINDER: clean this class when technicalInfo AB test is over (only 1 class)
      [styles.emptyTechnicalInfoTag]:
        technicalInfoEnabled && !thirdLineLabels?.length, // REMINDER: clean this class when technicalInfo AB test is over (only 1 class)
    },
    className,
  );

  const translationForDetailedProps: DetailedPriceTranslations = {
    price: translations.price,
  };

  return React.createElement(
    tag,
    {
      className: rootClassNames,
      ref,
      title,
      onClick,
      ...tagProps,
      ...otherProps,
    },
    <>
      {topLeftLabel && (
        <ProductTags
          className={styles.topLeftLabel}
          tags={topLeftLabel}
          translations={
            translations.tags as ProductCardListingsTranslations['tags']
          }
        />
      )}

      <ProductImage
        className={styles.image}
        alt={title}
        src={imageSrc}
        srcSet={imageSrcSet}
        type={type}
        image={productImage || ImageComponent}
        loading={imageLoading}
        provideDataSrc={provideImageDataSrc}
      />

      {modelsCount > 1 && (
        <ModelVariations
          className={styles.modelVariations}
          modelsCount={modelsCount}
          translations={translations.tags.options}
        />
      )}

      {(brandImageSrc || ['fr'].includes(madeIn || '') || ecoResponsible) &&
        brandImageSrc && (
          <BrandImage
            className={styles.brand}
            alt={brandName}
            src={brandImageSrc}
            image={ImageComponent}
            loading={imageLoading}
          />
        )}

      {middleLabels && (
        <ProductTags
          className={styles.middleLabels}
          promoted={promoted}
          tags={middleLabels}
          secondHand={secondHand}
          translations={
            translations.tags as ProductCardListingsTranslations['tags']
          }
          onAdvertiserLabelClicked={onAdvertiserLabelClicked}
        />
      )}

      <Title
        className={styles.title}
        text={title}
        promoted={promoted}
        displayThreeLines={technicalInfoEnabled && !thirdLineLabels?.length}
      />

      {technicalInfoEnabled && !!thirdLineLabels?.length && (
        <ProductTags
          className={styles.thirdLineLabels}
          tags={thirdLineLabels}
          translations={
            translations.tags as ProductCardListingsTranslations['tags']
          }
        />
      )}

      <Ratings
        className={styles.ratings}
        nbStars={rating}
        count={ratingCount === 0 || !ratingCount ? undefined : `${ratingCount}`}
        size="sm"
        theme="fill"
      />

      <DetailedPrice
        className={styles.price}
        currency={currency}
        value={Number(price)}
        secondaryValue={secondaryPrice}
        secondaryUnit={secondaryUnit}
        tax={tax}
        retailValue={retailPrice}
        translations={translationForDetailedProps}
        retailPriceTimeLeftLabel={retailPriceTimeLeftLabel}
        promo={promo}
      />

      {legalMention && (
        <LegalMention
          className={styles.legalMention}
          text={
            (legalMentionNoVAT && translations.legalMention.mentionNoVAT) ||
            translations.legalMention.mention
          }
        />
      )}

      {delivery && (
        <DeliveryLabel
          className={styles.delivery}
          deliveryLabel={delivery}
          translations={translations.deliveryLabel}
        />
      )}

      {promoted && (
        <AddToCartButton
          className={styles.addToCart}
          text={translations.addToCart.label}
          onClick={onAddToCart}
          theme={b2b ? 'pro' : 'default'}
        />
      )}
    </>,
  );
}

export const ProductCardListings = forwardRef(InnerProductCardListings) as <
  Tag extends React.ElementType = typeof DEFAULT_TAG,
>(
  props: ProductCardListingsProps<Tag> & RefAttributes<React.ElementRef<Tag>>,
) => ReactElement;

(ProductCardListings as React.FC).displayName = 'ProductCardListings';
